// Yoga Beckington - Village Location Page
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Car } from "lucide-react";
import Navigation from "../../components/shared/Navigation";
import Footer from "../../components/shared/Footer";
import PageBreadcrumb from "../../components/shared/PageBreadcrumb";
import { RetreatPromoBanner } from "../../components/shared/RetreatPromo";
import { usePageMeta, generateLocationSchema, generateFAQSchema, SchemaScript } from "../../components/shared/SEOHelper";

const YogaBeckington = () => {
  usePageMeta({
    title: "Yoga Classes Beckington | Annelie Lily",
    description: "Yoga classes for Beckington, Somerset. Small group yoga in Frome, just 10 minutes from Beckington village on the A36. All levels welcome.",
    canonical: "https://annelielily.com/areas/yoga-beckington"
  });

  const locationSchema = generateLocationSchema({
    townName: "Beckington",
    description: "Yoga classes for Beckington village at Annelie Lily Yoga in Frome. Just 10 minutes away via the A36.",
    url: "https://annelielily.com/yoga-beckington"
  });

  const faqs = [
    { question: "How do I get to Frome from Beckington?", answer: "Head south on the A36, then into Frome — about 10 minutes. It's the same road towards Bath, just turn off earlier." },
    { question: "What styles of yoga do you teach?", answer: "Hatha yoga (traditional, active practice), Yin yoga (slow, deep stretches), Aerial yoga (using hammocks), and private sessions." },
    { question: "Can I try a class before committing?", answer: "Of course. Come to a single class first and see how you feel. There's no pressure to book a block." },
  ];

  const faqSchema = generateFAQSchema(faqs);

  return (
    <div className="min-h-screen bg-cream">
      <SchemaScript schema={locationSchema} />
      <SchemaScript schema={faqSchema} />
      <Navigation />
      <PageBreadcrumb currentPage="Yoga in Beckington" currentSlug="areas/yoga-beckington" />
      
      <section className="pt-8 pb-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">Yoga for Beckington</span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-neutral-800 mb-5">Yoga Classes Beckington</h1>
            <p className="text-neutral-700 leading-relaxed max-w-2xl mx-auto mb-6">
              Beckington sits right on the A36 between Frome and Bath. My studio is just 10 minutes 
              south — closer than driving into Bath, with smaller classes and more personal attention.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700"><MapPin className="w-4 h-4 text-gold" />Based in Frome</span>
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700"><Car className="w-4 h-4 text-gold" />10 min from Beckington</span>
            </div>
            <Link to="/contact" className="btn-primary">Book a Class <ArrowRight className="w-4 h-4 inline ml-2" /></Link>
          </motion.div>
        </div>
      </section>
      
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">Yoga for Beckington Residents</h2>
          <div className="prose-content space-y-4 text-neutral-700">
            <p>
              Living in Beckington, you're well-placed for yoga in Frome. The A36 makes it a 
              quick 10-minute journey, and you'll find something quite different from the 
              busy gym classes in <Link to="/areas/yoga-bath" className="text-gold hover:underline">Bath</Link>.
            </p>
            <p>
              My <Link to="/yoga-classes" className="text-gold hover:underline">yoga classes</Link> are 
              kept small — 6-8 people — so there's actually space for individual attention. 
              Whether you want <Link to="/yoga-classes/hatha-yoga" className="text-gold hover:underline">Hatha yoga</Link> to 
              build strength or <Link to="/yoga-classes/yin-yoga" className="text-gold hover:underline">Yin yoga</Link> for 
              deep release, you'll find a calm, supportive environment.
            </p>
            <p>
              <Link to="/yoga-classes/beginner-yoga" className="text-gold hover:underline">Complete beginner?</Link> That's 
              no problem — I offer modifications for every pose and teach at a pace that welcomes all levels.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-10 text-center">Yoga Classes for Beckington</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: "Hatha Yoga", desc: "Traditional practice building strength and flexibility.", link: "/yoga-classes/hatha-yoga" },
              { title: "Yin Yoga", desc: "Slow, deep stretches for tension release.", link: "/yoga-classes/yin-yoga" },
              { title: "Private Sessions", desc: "One-to-one yoga tailored to you.", link: "/yoga-classes/private-yoga" },
            ].map((service) => (
              <Link key={service.title} to={service.link} className="block bg-white/70 p-6 rounded-xl border border-gold/10 hover:border-gold/30 transition-all group">
                <h3 className="font-heading text-xl text-neutral-800 mb-2 group-hover:text-gold">{service.title}</h3>
                <p className="text-neutral-600 text-sm mb-3">{service.desc}</p>
                <span className="text-gold text-sm font-medium">Learn more →</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8 text-center">Questions from Beckington Residents</h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white/70 p-5 rounded-xl border border-gold/10">
                <h3 className="font-heading text-lg text-neutral-800 mb-2">{faq.question}</h3>
                <p className="text-neutral-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">Ready to Start?</h2>
          <p className="text-neutral-600 mb-6">Get in touch and I'll help you find the right class.</p>
          <Link to="/contact" className="btn-primary">Book a Class <ArrowRight className="w-4 h-4 inline ml-2" /></Link>
        </div>
      </section>

      <section className="py-12 px-6 md:px-12 bg-cream">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-neutral-600 text-sm mb-4">Nearby areas:</p>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { name: "Bath", link: "/areas/yoga-bath" },
              { name: "Trowbridge", link: "/areas/yoga-trowbridge" },
              { name: "Mells", link: "/areas/yoga-mells" },
              { name: "Nunney", link: "/areas/yoga-nunney" },
              { name: "Somerset", link: "/areas/yoga-somerset" },
            ].map((area) => (
              <Link key={area.name} to={area.link} className="text-sm text-gold hover:underline">{area.name}</Link>
            ))}
          </div>
        </div>
      </section>

      {/* Retreat Promotion */}
      <RetreatPromoBanner variant="highlight" />

      <Footer />
    </div>
  );
};

export default YogaBeckington;
